import { parsePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input'

/**
 * Phone numbers are stored WITHOUT the country code: the national number
 * ("8287259604") goes in the number column and the dial code ("91") in its
 * own column. Every form that collects a number through
 * react-phone-number-input holds it in E.164 ("+918287259604"), so these two
 * helpers are the single place where that value is split on the way out and
 * rebuilt on the way in.
 */

/**
 * Splits an E.164 value into its dial code and national number.
 * Returns null when the value can't be parsed, so callers can surface their
 * own "Phone Number is not valid" error instead of sending junk.
 *
 *   splitPhone('+918287259604') -> { countryCode: '91', nationalNumber: '8287259604' }
 */
export const splitPhone = (value) => {
    const parsed = parsePhoneNumber(String(value || '').trim())
    if (!parsed) return null
    return {
        countryCode: parsed.countryCallingCode,
        nationalNumber: parsed.nationalNumber,
    }
}

/**
 * Rebuilds the E.164 string the phone input expects from a stored number +
 * dial code.
 *
 * Records written before the split — where the dial code was concatenated
 * onto the number ("918287259604") — are still in the database, so a stored
 * value that already carries its own code is detected and not double-prefixed.
 * The national reading wins whenever it is a valid number, which keeps
 * national numbers that merely happen to start with the dial code's digits
 * intact.
 */
export const toE164 = (phone, code = '91') => {
    if (!phone) return ''
    const raw = String(phone).trim()
    if (raw.startsWith('+')) return raw
    const digits = raw.replace(/\D/g, '')
    if (!digits) return ''
    const dial = String(code || '').replace('+', '').trim()
    if (!dial) return '+' + digits

    const prefixed = '+' + dial + digits
    if (isValidPhoneNumber(prefixed)) return prefixed
    // Legacy row: the code is already part of the stored number.
    if (digits.startsWith(dial)) {
        const asStored = '+' + digits
        if (isValidPhoneNumber(asStored)) return asStored
    }
    return prefixed
}
